import { isProvider } from '@bariendo/lib/profile';
import { ActionIcon, Group, Image, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { SignInForm, useMedplum } from '@medplum/react';
import { useLocation, useNavigate } from 'react-router';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { IconArrowLeft } from '@tabler/icons-react';
import logoBlack from '../assets/everself_logo_black.png';

export function SignInPage(): ReactElement {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || '/';
  // we can reset the form, to go back to the "enter email" page
  const [formKey, setFormKey] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleSignInSuccess = async (): Promise<void> => {
    const profile = medplum.getProfile();

    if (!isProvider(profile)) {
      notifications.show({
        title: 'Access Denied',
        message: 'Only healthcare providers can access this application.',
        color: 'red',
        autoClose: 5000,
      });

      await medplum.signOut();
      navigate('/', { replace: true });
      return;
    }

    navigate(from, { replace: true });
  };

  useEffect(() => {
    const checkHasPassword = () => {
      const root = wrapperRef.current;
      const hasPwd = !!root?.querySelector('input[type="password"]');
      setShowBack(hasPwd);
    };

    checkHasPassword();

    const observer = new MutationObserver(() => {
      checkHasPassword();
    });
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, { childList: true, subtree: true, attributes: true });
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapperRef}>
      <SignInForm
        key={formKey}
        onSuccess={handleSignInSuccess}
        onForgotPassword={() => navigate('/resetpassword')}
        googleClientId={import.meta.env?.VITE_GOOGLE_OAUTH_CLIENT_ID}
      >
        {showBack && (
          <Group w="100%" justify="flex-start" mb="xs">
            <ActionIcon variant="subtle" size="sm" aria-label="Back" onClick={() => setFormKey((k) => k + 1)}>
              <IconArrowLeft size={16} />
            </ActionIcon>
          </Group>
        )}
        <Image src={logoBlack} alt="EverSelf Logo" w={180} />
        <Title mt="md" fz="xl">
          Sign in to Orbit
        </Title>
      </SignInForm>
    </div>
  );
}
